// Passive items. Each owned passive level contributes to a single Modifiers struct
// that the weapon system and player read. Recomputed only when a passive changes
// (on draft pick), never per frame.

import { CRIT } from './balance';
import { VARIANT } from './variant';

export interface Modifiers {
  damageMul: number;
  cooldownMul: number;
  areaMul: number;
  countAdd: number;
  moveSpeedMul: number;
  magnetAdd: number;
  maxHpAdd: number;
  regenPerSec: number;
  critChance: number;
  critMult: number;
  splashFrac: number;
  splashRadius: number;
  /** 0..1 fraction of the puddle "wet" slow that is ignored (NinjaKid's Aqua). */
  wetResist: number;
}

export const neutralMods = (): Modifiers => ({
  damageMul: 1,
  cooldownMul: 1,
  areaMul: 1,
  countAdd: 0,
  moveSpeedMul: 1,
  magnetAdd: 0,
  maxHpAdd: 0,
  regenPerSec: 0,
  critChance: CRIT.baseChance,
  critMult: CRIT.baseMult,
  splashFrac: 0,
  splashRadius: 0,
  wetResist: 0,
});

export interface PassiveDef {
  key: string;
  name: string;
  maxLevel: number;
  desc: string;
  color?: number;
}

export const BASE_PASSIVE_KEYS = [
  'pouch', 'hourglass', 'quiver', 'lens', 'lucky',
  'volatile', 'boots', 'heart', 'lodestone', 'salve',
] as const;

export const VERDANT_PASSIVE_KEYS = [
  'potentMash', 'quickFerment', 'seedSatchel', 'myceliumLens', 'luckyClover',
  'burstPods', 'rootstepSoles', 'barkskin', 'pollenCharm', 'royalJelly',
] as const;

export const STARFALL_PASSIVE_KEYS = [
  'meteorCore', 'clockworkEscapement', 'twinCapacitor', 'graviticLens', 'starChart',
  'shrapnelCasing', 'railSkates', 'alloyPlating', 'fluxMagnet', 'starlightReservoir',
] as const;

export const NINJA_PASSIVE_KEYS = [
  'starco', 'swich', 'duplicate', 'bigChanger', 'ninjaSpark',
  'bombCubes', 'aqua', 'beast', 'gummyBean', 'curvy',
] as const;

export const PASSIVE_DEFS: Record<string, PassiveDef> = {
  pouch: { key: 'pouch', name: 'Reagent Pouch', maxLevel: 5, desc: '+12% damage' },
  hourglass: { key: 'hourglass', name: 'Hourglass', maxLevel: 5, desc: '-8% cooldown (faster firing)' },
  quiver: { key: 'quiver', name: 'Quiver', maxLevel: 5, desc: '+1 projectile' },
  lens: { key: 'lens', name: 'Lens', maxLevel: 5, desc: '+12% area / size' },
  lucky: { key: 'lucky', name: 'Lucky Vial', maxLevel: 5, desc: '+6% crit chance' },
  volatile: { key: 'volatile', name: 'Volatile Reagents', maxLevel: 5, desc: 'hits splash nearby enemies' },
  boots: { key: 'boots', name: 'Swift Boots', maxLevel: 5, desc: '+10% move speed' },
  heart: { key: 'heart', name: "Alchemist's Heart", maxLevel: 5, desc: '+20 max HP' },
  lodestone: { key: 'lodestone', name: 'Lodestone', maxLevel: 5, desc: '+30 magnet range' },
  salve: { key: 'salve', name: 'Regen Salve', maxLevel: 5, desc: '+0.6 HP/sec' },

  // The Verdant Still: living ingredients and fermentation tools.
  potentMash: { key: 'potentMash', name: 'Potent Mash', maxLevel: 5, desc: '+12% damage', color: 0xb9e84f },
  quickFerment: { key: 'quickFerment', name: 'Quick Ferment', maxLevel: 5, desc: '-8% cooldown', color: 0xffc35a },
  seedSatchel: { key: 'seedSatchel', name: 'Seed Satchel', maxLevel: 5, desc: '+1 projectile', color: 0x8ed052 },
  myceliumLens: { key: 'myceliumLens', name: 'Mycelium Lens', maxLevel: 5, desc: '+12% area / size', color: 0xd680d8 },
  luckyClover: { key: 'luckyClover', name: 'Lucky Clover', maxLevel: 5, desc: '+6% crit chance', color: 0xa6f2dc },
  burstPods: { key: 'burstPods', name: 'Burst Pods', maxLevel: 5, desc: 'hits burst into nearby creatures', color: 0xf35fca },
  rootstepSoles: { key: 'rootstepSoles', name: 'Rootstep Soles', maxLevel: 5, desc: '+10% move speed', color: 0x79b84f },
  barkskin: { key: 'barkskin', name: 'Barkskin', maxLevel: 5, desc: '+20 max HP', color: 0xc8873f },
  pollenCharm: { key: 'pollenCharm', name: 'Pollen Charm', maxLevel: 5, desc: '+30 pickup range', color: 0xffdf78 },
  royalJelly: { key: 'royalJelly', name: 'Royal Jelly', maxLevel: 5, desc: '+0.6 HP/sec', color: 0xffb84f },

  // The Starfall Conduit: machined components and astronomical instruments.
  meteorCore: { key: 'meteorCore', name: 'Meteor Core', maxLevel: 5, desc: '+12% damage', color: 0xe55b4f },
  clockworkEscapement: { key: 'clockworkEscapement', name: 'Clockwork Escapement', maxLevel: 5, desc: '-8% cooldown', color: 0xd6a85f },
  twinCapacitor: { key: 'twinCapacitor', name: 'Twin Capacitor', maxLevel: 5, desc: '+1 projectile', color: 0x74edff },
  graviticLens: { key: 'graviticLens', name: 'Gravitic Lens', maxLevel: 5, desc: '+12% area / size', color: 0x8bb5ff },
  starChart: { key: 'starChart', name: 'Star Chart', maxLevel: 5, desc: '+6% crit chance', color: 0xb2cdff },
  shrapnelCasing: { key: 'shrapnelCasing', name: 'Shrapnel Casing', maxLevel: 5, desc: 'hits scatter into nearby constructs', color: 0xff8a45 },
  railSkates: { key: 'railSkates', name: 'Rail Skates', maxLevel: 5, desc: '+10% move speed', color: 0x60d8e8 },
  alloyPlating: { key: 'alloyPlating', name: 'Alloy Plating', maxLevel: 5, desc: '+20 max HP', color: 0x8b9398 },
  fluxMagnet: { key: 'fluxMagnet', name: 'Flux Magnet', maxLevel: 5, desc: '+30 pickup range', color: 0xec78e1 },
  starlightReservoir: { key: 'starlightReservoir', name: 'Starlight Reservoir', maxLevel: 5, desc: '+0.6 HP/sec', color: 0xf2d38a },

  // NinjaKid Dojo: the kid's sketchbook upgrades, spelled his way.
  starco: { key: 'starco', name: 'Starco', maxLevel: 5, desc: '+12% damage (blue storm power)', color: 0xd94a5e },
  swich: { key: 'swich', name: 'Swich', maxLevel: 5, desc: '-8% cooldown (double lightning)', color: 0xa8e05f },
  duplicate: { key: 'duplicate', name: 'Duplicate', maxLevel: 5, desc: '+1 projectile (double you)', color: 0x9e97a8 },
  bigChanger: { key: 'bigChanger', name: 'Big Changer', maxLevel: 5, desc: '+12% area (make it BIG)', color: 0xb07fe8 },
  ninjaSpark: { key: 'ninjaSpark', name: 'Spark', maxLevel: 5, desc: '+6% crit chance (sparks orbit you)', color: 0xcda8ff },
  bombCubes: { key: 'bombCubes', name: 'Bomb Cubes', maxLevel: 5, desc: 'hits explode into nearby foes', color: 0xe0566e },
  aqua: { key: 'aqua', name: 'Aqua', maxLevel: 5, desc: '+10% move speed; water slows you less', color: 0x4fb0e0 },
  beast: { key: 'beast', name: 'Beast', maxLevel: 5, desc: '+20 max HP (shrug off hits)', color: 0xe8863c },
  gummyBean: { key: 'gummyBean', name: 'Gummy Bean', maxLevel: 5, desc: '+30 pickup range (rubbery stretch)', color: 0xf06292 },
  curvy: { key: 'curvy', name: 'Curvy', maxLevel: 5, desc: '+0.6 HP/sec (health goes up endlessly)', color: 0x64b5f6 },
};

export function computeModifiers(levels: ReadonlyMap<string, number>): Modifiers {
  const m = neutralMods();
  const lv = (k: string): number => levels.get(k) ?? 0;
  const total = (...keys: string[]): number => keys.reduce((sum, key) => sum + lv(key), 0);

  m.damageMul += 0.12 * total('pouch', 'potentMash', 'meteorCore', 'starco');
  m.cooldownMul = Math.max(0.4, 1 - 0.08 * total('hourglass', 'quickFerment', 'clockworkEscapement', 'swich'));
  m.countAdd += total('quiver', 'seedSatchel', 'twinCapacitor', 'duplicate');
  m.areaMul += 0.12 * total('lens', 'myceliumLens', 'graviticLens', 'bigChanger');
  m.critChance += 0.06 * total('lucky', 'luckyClover', 'starChart', 'ninjaSpark');
  const splashLevel = total('volatile', 'burstPods', 'shrapnelCasing', 'bombCubes');
  if (splashLevel > 0) {
    m.splashFrac = 0.35 + 0.1 * (splashLevel - 1);
    m.splashRadius = 34 + 8 * (splashLevel - 1);
  }
  m.moveSpeedMul += 0.1 * total('boots', 'rootstepSoles', 'railSkates', 'aqua');
  // Aqua also shrugs off the puddle slow - 20% less per level, immune at max.
  m.wetResist = Math.min(1, 0.2 * lv('aqua'));
  // Alchemist's Heart: +max HP on the base game; on /hidden it's "Alchemists Hard" (+damage).
  if (VARIANT === 'hidden') m.damageMul += 0.15 * lv('heart');
  else m.maxHpAdd += 20 * lv('heart');
  // Expansion HP passives keep their advertised effect in both page variants.
  m.maxHpAdd += 20 * total('barkskin', 'alloyPlating', 'beast');
  m.magnetAdd += 30 * total('lodestone', 'pollenCharm', 'fluxMagnet', 'gummyBean');
  m.regenPerSec += 0.6 * total('salve', 'royalJelly', 'starlightReservoir', 'curvy');

  return m;
}
